import React, { useEffect, useState } from "react";

import { useTheme } from "../../../context/ThemeContext";

import { dateFormat } from "../../../utils/dateFormat";
import { formatCurrencyAmount } from "../../../utils/formatContent";

import CtaButton from "../../shared/buttons/ctaButton";

/**
 * Generates the program date durations based on the program code.
 *
 * @param {string} programCode - The code of the program
 * @param {string} title - The title of the program
 * @param {string} description - The description of the program
 * @param {string} travelDesc - Optional description for travel discount
 * @param {string} applicationUrl - Optional URL for program application
 * @param {string} containerClass - Optional class for the container
 * @param {string} innerContainerClass - Optional class for the inner container
 * @param {string} titleClass - Optional class for the title
 * @param {object} programInclusions - The inclusions of the program
 * @return {JSX.Element} The program date durations section component
 */
const ProgramDateDurations = ({
  programCode,
  title,
  description,
  programInclusions,
  travelDesc = "Travel with a friend(s) and you'll all get 10% off your program fee.",
  applicationUrl = "#",
  innerContainerClass = "summary-section justify-center w-11/12 pt-2 sm:py-2 flex gap-10 flex-wrap sm:flex-nowrap sm:w-10/12 2xl:container 2xl:m-auto",
  innerContainerChildClass = "w-full md:sticky-left-section mr-auto md:!pr-4 border-t border-gray-generic dark:border-gray-standard dark:md:border-gray-light",
  outerContainerClass = "w-full bg-primary-bg mx-auto flex items-center justify-center relative overflow-hidden",
  titleClass = "font-light dark:font-bold text-xl sm:text-xlrg-sm dark:text-white pb-5",
  fromPrefix = ": From ",
  showdayPrice = true,
  showDatesSummary = false
}) => {
  const { currentSiteMeta } = useTheme();

  const currency =
    currentSiteMeta.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";
  const currencyToUse = currency
    ? currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const [dates, setDates] = useState([]);
  const [groupedDates, setGroupedDates] = useState([]);

  /**
   * Returns the plural form of a word if the given word is not equal to 1 and does not already end with an "s".
   *
   * @param {string} word - The word to be pluralized.
   * @return {string} The plural form of the word, or the original word if it is equal to 1 or already ends with an "s".
   */
  const makePlural = (word) => {
    if (parseInt(word) > 1 && !/s$/i.test(word)) return word + "s";

    return word;
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getdates = async () => {
      const res = await fetch(
        `/api/salesforce/programdates?program_code=${programCode}`,
        { signal: signal }
      );
      const durations = await res.json();

      if (!durations) {
        // console.error("no durations", durations);
        return [];
      }

      const dateGroup = durations.reduce(
        (
          prevVal,
          currentVal,
          currentIndex,
          theArray,
          keyVal = currentVal.InstanceDate
        ) => (
          (prevVal[keyVal] || (prevVal[keyVal] = [])).push(currentVal), prevVal
        ),
        {}
      );

      let durationList = [];
      let datesGroupList = [];
      Object.keys(dateGroup).forEach((date) => {
        const year = date.substring(0, 4);
        if (!datesGroupList[year]) {
          datesGroupList[year] = [];
        }

        datesGroupList[year].push(
          dateFormat(
            {
              month: "short",
              day: "numeric"
            },
            Date.parse(date)
          )
        );

        dateGroup[date]?.forEach((dateItem) => {
          durationList[dateItem.Duration] = {
            title: makePlural(dateItem?.DurationDesc?.toLowerCase()),
            price: dateItem?.ProductPrices?.filter(
              (item) =>
                item?.CurrencyIso?.toLowerCase() ===
                currencyToUse?.toLowerCase()
            )?.shift(),
            debug_data: dateItem
          };
        });
      });

      setGroupedDates(datesGroupList);
      setDates(durationList);
    };

    if (programCode) {
      getdates().catch(console.error);
    } else {
      return () => {
        controller.abort();
      };
    }

    return () => {
      controller.abort();
    };
  }, [currencyToUse, programCode]);

  if (dates?.length < 1) {
    return <></>;
  }

  return (
    <section className={outerContainerClass}>
      <section className={innerContainerClass}>
        <div className={innerContainerChildClass}>
          <div className="grid gap-6 sm:gap-20 grid-cols-1 w-full">
            <div className="w-full pt-12">
              <h6
                className={titleClass}
                dangerouslySetInnerHTML={{ __html: title }}
              />

              {description && (
                <div
                  className="price-duration-desc pb-5 text-generic-text dark:text-gray-generic text-mob sm:text-base"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}

              <div
                className={
                  "rounded-md w-full md:w-3/4 h-auto max-h-102 overflow-y-scroll border border-gray-generic dark:border-gray-light"
                }
              >
                {dates?.map((duration, index) => {
                  return (
                    <div
                      key={`duration-${index}`}
                      className={
                        "block w-full overflow-hidden text-base odd:bg-white odd:dark:bg-gray-dark even:bg-yellow-lighter even:dark:bg-generic-bg"
                      }
                    >
                      <div
                        className={`h-12 text-center w-full flex items-center justify-between text-generic-text dark:text-gray-generic text-mob sm:text-base px-4 sm:pl-5 sm:pr-8`}
                      >
                        <div
                          className={
                            "font-normal text-center flex items-center justify-center "
                          }
                          dangerouslySetInnerHTML={{
                            __html: `${makePlural(duration?.title?.toLowerCase())}${fromPrefix}${formatCurrencyAmount(
                              {
                                currentSiteMeta,
                                amount: duration?.price?.MinimumPrice
                              }
                            )}`
                          }}
                        />
                        {showdayPrice && (
                          <div
                            className={"font-bold  dark:text-white"}
                            dangerouslySetInnerHTML={{
                              __html: `${formatCurrencyAmount({
                                currentSiteMeta,
                                amount: duration?.price?.DailyPrice
                              })} per day`
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {showDatesSummary && (
            <div className="w-full py-12 sm:pb-0">
              <div className="pb-4 flex flex-wrap gap-2 items-center">
                <div className="h3">Start dates</div>
                <div className="text-sm text-black-lighter">
                  (Programs start every first and third Saturday of the month)
                </div>
              </div>
              {groupedDates && groupedDates?.length > 0 && (
                <div className="flex flex-col gap-2">
                  {groupedDates?.map((date, index) => {
                    return (
                      <div
                        key={`date-year-${index}`}
                        className="flex flex-col gap-2 divide-y divide-solid divide-gray-generic dark:divide-gray-light"
                      >
                        <div
                          className="font-normal"
                          dangerouslySetInnerHTML={{ __html: index }}
                        />
                        <div
                          className="text-sm text-black-lighter py-2"
                          dangerouslySetInnerHTML={{ __html: date?.join(", ") }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className={"hidden sm:block py-16 w-full md:w-3/4"}>
            <CtaButton
              ctaLabel={`Apply`}
              preset={"primary"}
              labelClass="text-base leading-genericCta pr-2 font-bold"
              extraButtonStyle={`w-full sm:w-56 min-w-56 ${showDatesSummary ? "" : "m-auto"}`}
              showAsButton={false}
              to={applicationUrl}
            />
          </div>
        </div>
      </section>
    </section>
  );
};

export default ProgramDateDurations;
